import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CreatePimsFormProps } from './types';
import { createPimsSchema, createPimsFields } from './schema';
import type { CreatePimsFormValues } from './schema';
import { styles } from './styles';
import { Modal, SchemaForm, Checkbox } from '@v2/components';
import type { FieldDefinition } from '@v2/components/SchemaForm/types';
import { useCreatePims, useApps } from '@v2/hooks';
import type { CreatePimsPayload } from '@v2/types';

const CreatePimsForm: React.FC<CreatePimsFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const { createPims, loading, error: createError } = useCreatePims();
  const { apps, loading: appsLoading } = useApps();
  const [supportsConnectivity, setSupportsConnectivity] = useState(false);

  const fieldsWithApps: FieldDefinition[] = useMemo(
    () =>
      createPimsFields.map(field =>
        field.name === 'appId'
          ? {
              ...field,
              options: apps.map(app => ({ value: app.id, label: app.name })),
              disabled: appsLoading,
            }
          : field,
      ),
    [apps, appsLoading],
  );

  const handleSubmit = async (data: CreatePimsFormValues): Promise<void> => {
    const payload: CreatePimsPayload = {
      name: data.name,
      appId: data.appId,
      supportsConnectivity,
    };

    try {
      const newId = await createPims(payload);
      toast.success('PIMS created successfully');
      onSuccess?.(newId);
      onClose();
    } catch {
      toast.error(createError ?? 'Failed to create PIMS');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create PIMS" size="md">
      <div className={styles.formContainer}>
        <div className={styles.connectivityWrapper}>
          <Checkbox
            label="Supports Connectivity"
            checked={supportsConnectivity}
            onChange={setSupportsConnectivity}
          />
        </div>
        <SchemaForm
          fields={fieldsWithApps}
          schema={createPimsSchema}
          onSubmit={handleSubmit}
          loading={loading || appsLoading}
          error={createError}
          submitLabel="Create"
          onCancel={onClose}
          cancelLabel="Cancel"
        />
      </div>
    </Modal>
  );
};

export { CreatePimsForm };
export default CreatePimsForm;
